import { useEffect, useMemo, useRef, useState } from "react";

const POSTER_DESKTOP = "/hero-poster.webp";
const POSTER_MOBILE = "/hero-poster-mobile.webp";

// 16:9 landscape clips for wide-desktop cycling.
// Two sets — /home (default, used on the marketing landing page) and
// /product (the previous video set, still used by ProductHero). The
// active set is selected via the `variant` prop on <HeroVideoBG />.
//
// HOME set — 18 new clips, optimized to ~500 KB-1.5 MB each (h264 high
// @ 720p 24 fps CRF 28 + faststart). Ordered by file size ascending so
// the FIRST clip is the lightest = fastest first paint.
//   hero-12 518K · hero-11 557K · hero-13 579K · hero-5 610K ·
//   hero-17 659K · hero-10 706K · hero-1 712K · hero-18 772K ·
//   hero-8 790K · hero-2 823K · hero-9 851K · hero-4 869K ·
//   hero-15 871K · hero-3 938K · hero-14 1.0M · hero-6 1.2M ·
//   hero-16 1.5M · hero-7 1.6M
// Posters switched to AVIF (all clips now have .avif generated at CRF 38 via
// libsvtav1). Saves ~55-60% vs WebP on the cycling clips (22-56 KB vs 50-69 KB).
// AVIF support is 97%+ on Chrome 85+, Firefox 93+, Safari 16+. Browsers that
// can't decode AVIF will show a black poster momentarily before playback starts
// (no regression on the LCP clip — that's preloaded as AVIF in index.html
// which already has the type="image/avif" guard and a picture-element fallback
// in place for older browsers).
const HOME_SOURCES: ClipSource[] = [
  { src: "/hero/home/hero-12.mp4", poster: "/hero/home/hero-12-poster.avif" },
  { src: "/hero/home/hero-11.mp4", poster: "/hero/home/hero-11-poster.avif" },
  { src: "/hero/home/hero-13.mp4", poster: "/hero/home/hero-13-poster.avif" },
  { src: "/hero/home/hero-5.mp4",  poster: "/hero/home/hero-5-poster.avif"  },
  { src: "/hero/home/hero-17.mp4", poster: "/hero/home/hero-17-poster.avif" },
  { src: "/hero/home/hero-10.mp4", poster: "/hero/home/hero-10-poster.avif" },
  { src: "/hero/home/hero-1.mp4",  poster: "/hero/home/hero-1-poster.avif"  },
  { src: "/hero/home/hero-18.mp4", poster: "/hero/home/hero-18-poster.avif" },
  { src: "/hero/home/hero-8.mp4",  poster: "/hero/home/hero-8-poster.avif"  },
  { src: "/hero/home/hero-2.mp4",  poster: "/hero/home/hero-2-poster.avif"  },
  { src: "/hero/home/hero-9.mp4",  poster: "/hero/home/hero-9-poster.avif"  },
  { src: "/hero/home/hero-4.mp4",  poster: "/hero/home/hero-4-poster.avif"  },
  { src: "/hero/home/hero-15.mp4", poster: "/hero/home/hero-15-poster.avif" },
  { src: "/hero/home/hero-3.mp4",  poster: "/hero/home/hero-3-poster.avif"  },
  { src: "/hero/home/hero-14.mp4", poster: "/hero/home/hero-14-poster.avif" },
  { src: "/hero/home/hero-6.mp4",  poster: "/hero/home/hero-6-poster.avif"  },
  { src: "/hero/home/hero-16.mp4", poster: "/hero/home/hero-16-poster.avif" },
  { src: "/hero/home/hero-7.mp4",  poster: "/hero/home/hero-7-poster.avif"  },
];

// PRODUCT set — the previous /hero/hero-*.mp4 clips, now living under
// /hero/product/ and used by ProductHero on /product. Same encoding
// profile (h264 720p 24 fps), sizes 824 KB - 2.3 MB.
const PRODUCT_SOURCES: ClipSource[] = [
  { src: "/hero/product/hero-2.mp4" },
  { src: "/hero/product/hero-1.mp4" },
  { src: "/hero/product/hero-4.mp4" },
  { src: "/hero/product/hero-3.mp4" },
  { src: "/hero/product/hero-8.mp4" },
  { src: "/hero/product/hero-6.mp4" },
];

// DESKTOP_SOURCES is kept as a reference to the variant selected at
// mount. The component's `variant` prop ("home" | "product") chooses
// between HOME_SOURCES and PRODUCT_SOURCES. Default is "home".
const DESKTOP_SOURCES: Record<"home" | "product", ClipSource[]> = {
  home: HOME_SOURCES,
  product: PRODUCT_SOURCES,
};

// 9:16 portrait clips for mobile / coarse-pointer viewports.
//
// HOW TO USE:
//   1. Drop your 9:16 .mp4 files in /public/hero/mobile/. Suggested
//      encoding: H.264 high profile, 720x1280, 1.0–1.5 Mbps, faststart
//      (ffmpeg flag -movflags +faststart so the moov atom is at the
//      front and playback starts before the whole file downloads).
//   2. Generate a poster (frame 1) per clip with the helper script:
//        node scripts/scan-mobile-hero.cjs
//      That walks /public/hero/mobile/*.mp4 and writes a matching
//      -poster.webp for each (720x1280, ~25–40 KB).
//   3. Add each clip to the array below. ORDER MATTERS — the first
//      entry is the first clip the user sees, so put the lightest /
//      most impactful one first.
//
// LOADING BEHAVIOR (mobile-cycle tier):
//   • Exactly ONE clip's bytes ever in flight at a time. The next
//     clip's <video> element exists with preload="none" until the
//     current clip hits 50% playback — then we promote it to
//     preload="auto" + .load() in a single beat.
//   • Cross-fade between A and B is 350 ms; the last drawn frame
//     stays visible during the swap so there's never a black flash.
//   • Save-Data / 2g users skip cycling and see the poster image
//     only. 3g users get the first clip on infinite loop.
//
// FALLBACK: an empty mobile set for a variant makes that variant's
// mobile viewers fall back to a single looping clip from the same
// variant's desktop set. The deploy never breaks.
type ClipSource = { src: string; poster?: string };

// HOME mobile — 8 portrait clips (DO NOT REMOVE — Rick: full rotation required).
// Re-encoded at h264
// high@4.0 / CRF 30 / 24 fps / +faststart / no audio. Sizes
// 357 KB - 692 KB. Ordered lightest → heaviest so the FIRST clip
// (preloaded via <link rel="preload" as="video">) is the smallest
// = fastest LCP on cellular.
//   clip-5 357 K · clip-6 434 K · clip-7 476 K · clip-1 480 K ·
//   clip-8 563 K · clip-4 610 K · clip-2 679 K · clip-3 692 K
// Mobile posters also switched to AVIF — all 8 .avif files already existed.
const HOME_MOBILE_SOURCES: ClipSource[] = [
  { src: "/hero/mobile/home/clip-5.mp4", poster: "/hero/mobile/home/clip-5-poster.avif" },
  { src: "/hero/mobile/home/clip-6.mp4", poster: "/hero/mobile/home/clip-6-poster.avif" },
  { src: "/hero/mobile/home/clip-7.mp4", poster: "/hero/mobile/home/clip-7-poster.avif" },
  { src: "/hero/mobile/home/clip-1.mp4", poster: "/hero/mobile/home/clip-1-poster.avif" },
  { src: "/hero/mobile/home/clip-8.mp4", poster: "/hero/mobile/home/clip-8-poster.avif" },
  { src: "/hero/mobile/home/clip-4.mp4", poster: "/hero/mobile/home/clip-4-poster.avif" },
  { src: "/hero/mobile/home/clip-2.mp4", poster: "/hero/mobile/home/clip-2-poster.avif" },
  { src: "/hero/mobile/home/clip-3.mp4", poster: "/hero/mobile/home/clip-3-poster.avif" },
];

// PRODUCT mobile — previous 9 portrait clips, moved verbatim from
// /hero/mobile/ to /hero/mobile/product/ via git mv (same bytes,
// new path). Used by ProductHero's <HeroVideoBG variant="product" />.
const PRODUCT_MOBILE_SOURCES: ClipSource[] = [
  { src: "/hero/mobile/product/clip-1.mp4", poster: "/hero/mobile/product/clip-1-poster.webp" },
  { src: "/hero/mobile/product/clip-2.mp4", poster: "/hero/mobile/product/clip-2-poster.webp" },
  { src: "/hero/mobile/product/clip-3.mp4", poster: "/hero/mobile/product/clip-3-poster.webp" },
  { src: "/hero/mobile/product/clip-4.mp4", poster: "/hero/mobile/product/clip-4-poster.webp" },
  { src: "/hero/mobile/product/clip-5.mp4", poster: "/hero/mobile/product/clip-5-poster.webp" },
  { src: "/hero/mobile/product/clip-6.mp4", poster: "/hero/mobile/product/clip-6-poster.webp" },
  { src: "/hero/mobile/product/clip-7.mp4", poster: "/hero/mobile/product/clip-7-poster.webp" },
  { src: "/hero/mobile/product/clip-8.mp4", poster: "/hero/mobile/product/clip-8-poster.webp" },
  { src: "/hero/mobile/product/clip-9.mp4", poster: "/hero/mobile/product/clip-9-poster.webp" },
];

// Variant-aware lookup — same pattern as DESKTOP_SOURCES.
const MOBILE_SOURCES: Record<"home" | "product", ClipSource[]> = {
  home: HOME_MOBILE_SOURCES,
  product: PRODUCT_MOBILE_SOURCES,
};

// Connection / viewport tiering. Pick once per mount.
//   "skip"          — no video. Show poster only.
//                     (2g, slow-2g, save-data, prefers-reduced-motion)
//   "mobile-cycle"  — A/B cycle through MOBILE_SOURCES on coarse pointer
//                     or narrow viewport. Only used if MOBILE_SOURCES
//                     has entries. (4g+ mobile / tablet)
//   "single"        — ONE looping clip with the lightest desktop source.
//                     Fallback when MOBILE_SOURCES is empty, OR on 3g.
//   "full"          — A/B cycle through DESKTOP_SOURCES. (wifi/4g + wide)
type Tier = "skip" | "mobile-cycle" | "single" | "full";

const pickTier = (): Tier => {
  if (typeof window === "undefined") return "single";
  // @ts-expect-error - non-standard
  const c = (typeof navigator !== "undefined" ? navigator.connection : null) as
    | { saveData?: boolean; effectiveType?: string }
    | null;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "skip";
  if (c?.saveData) return "skip";
  if (typeof c?.effectiveType === "string" && /^(slow-2g|2g)$/.test(c.effectiveType)) return "skip";
  const coarse = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  const narrow = window.innerWidth < 768;
  // 3g — single looping clip, no cycle to save bandwidth.
  if (typeof c?.effectiveType === "string" && c.effectiveType === "3g") return "single";
  if (coarse || narrow) {
    // Mobile / coarse pointer. Both variants have portrait clips
    // uploaded, so always cycle. (The component below picks the
    // right set per variant; if a variant ever ships with no
    // mobile clips, the empty array would fall through to the
    // single tier — defensive only.)
    return "mobile-cycle";
  }
  return "full";
};

/**
 * Hero video background — four branches by tier:
 *
 *   skip          poster image only, no <video>.
 *                 (2g, save-data, prefers-reduced-motion)
 *
 *   mobile-cycle  A/B cross-fade through 9:16 portrait clips in
 *                 MOBILE_SOURCES. Back buffer stays preload="none"
 *                 until current clip is halfway through. Exactly ONE
 *                 clip's bytes in flight at a time.
 *                 (mobile / coarse pointer with 4g+, if you've
 *                 uploaded portrait clips)
 *
 *   single        ONE <video> looping the lightest desktop clip. No
 *                 cycle, no buffer.
 *                 (mobile fallback when MOBILE_SOURCES is empty;
 *                 also 3g everywhere)
 *
 *   full          A/B cycle through 16:9 landscape clips in
 *                 DESKTOP_SOURCES, eager buffer warming.
 *                 (wifi/4g + wide viewport)
 *
 * The poster image is set on every <video> element so the first paint
 * is always a real frame, never black.
 */
type Variant = "home" | "product";
/** Arm video `src` after idle or first gesture — poster paints first on mobile. */
const useDeferredMobileVideo = (enabled: boolean) => {
  const [armed, setArmed] = useState(() => !enabled);
  useEffect(() => {
    if (!enabled || armed) return;
    const arm = () => setArmed(true);
    type IdleWin = Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    const w = window as IdleWin;

    // Primary: arm after LCP fires (video bytes never compete with LCP poster).
    // Fallback chain: first scroll → requestIdleCallback(5 s) → setTimeout(4 s).
    // The short-timeout (2.5 s) idle used previously fired before LCP on
    // throttled connections, putting 385 KB of MP4 in the LCP critical path.
    let lcpObserver: PerformanceObserver | null = null;
    let fallbackId: number | undefined;
    let scrollFired = false;

    const armOnce = () => {
      if (scrollFired) return;
      scrollFired = true;
      lcpObserver?.disconnect();
      if (fallbackId !== undefined) {
        if (w.requestIdleCallback) w.cancelIdleCallback?.(fallbackId);
        else window.clearTimeout(fallbackId);
      }
      arm();
    };

    // LCP observer: fires as soon as browser emits last LCP entry.
    try {
      lcpObserver = new PerformanceObserver((list) => {
        if (list.getEntries().length > 0) armOnce();
      });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
    } catch {
      // PerformanceObserver not available — fall through to idle/scroll.
    }

    // Scroll gate: user starting to scroll means LCP has definitely fired.
    const opts = { once: true, passive: true } as AddEventListenerOptions;
    window.addEventListener("scroll", armOnce, opts);
    window.addEventListener("touchstart", armOnce, opts);
    window.addEventListener("pointerdown", armOnce, opts);

    // Safety fallback: arm after 4 s regardless (catch no-interaction pages).
    fallbackId = (w.requestIdleCallback
      ? w.requestIdleCallback(armOnce, { timeout: 4000 })
      : window.setTimeout(armOnce, 4000)) as number;

    return () => {
      scrollFired = true;
      lcpObserver?.disconnect();
      if (fallbackId !== undefined) {
        if (w.requestIdleCallback) w.cancelIdleCallback?.(fallbackId);
        else window.clearTimeout(fallbackId);
      }
      window.removeEventListener("scroll", armOnce);
      window.removeEventListener("touchstart", armOnce);
      window.removeEventListener("pointerdown", armOnce);
    };
  }, [enabled, armed]);
  return armed;
};

const HeroVideoBG = ({ variant = "home" }: { variant?: Variant } = {}) => {
  const tier = useMemo(pickTier, []);
  const isMobile = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    [],
  );
  const globalPoster = isMobile ? POSTER_MOBILE : POSTER_DESKTOP;
  // Poster-first on mobile: defer MP4 bytes until idle/gesture. All clips in
  // HOME_MOBILE_SOURCES / HOME_SOURCES still cycle once armed — no removals.
  const deferMobileBytes =
    tier === "mobile-cycle" || tier === "single";
  const videoArmed = useDeferredMobileVideo(isMobile && deferMobileBytes);

  // Pick the right portrait + landscape set per variant. /home
  // (default) uses the new clips; /product keeps the original.
  const desktopSet = DESKTOP_SOURCES[variant] ?? HOME_SOURCES;
  const mobileSet = MOBILE_SOURCES[variant] ?? HOME_MOBILE_SOURCES;
  const cycleSources = tier === "mobile-cycle" ? mobileSet : desktopSet;

  // A/B state. Both clip indexes (0-based into cycleSources) live
  // separately so the inactive buffer can race ahead.
  const [active, setActive] = useState<0 | 1>(0);
  const [idxA, setIdxA] = useState(0);
  const [idxB, setIdxB] = useState(1 % Math.max(1, cycleSources.length));
  const [warmBuffer, setWarmBuffer] = useState(false);
  const refA = useRef<HTMLVideoElement>(null);
  const refB = useRef<HTMLVideoElement>(null);
  const cursor = useRef(2);
  const activeRef = useRef<0 | 1>(0);

  const playSafely = (el: HTMLVideoElement | null) => {
    if (!el) return;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  // Guarantee the visible (active) video is always playing — covers
  // the case where the browser auto-paused a video when it became
  // opacity:0 then swapped to active, OR where the swap happened
  // before canplay fired. Runs on every active/idx change, and
  // re-arms after a delay in case the browser paused it after the
  // first play() call. Also kicks on first user interaction (touch
  // or pointermove) as a safety net for mobile autoplay policies.
  useEffect(() => {
    if (tier === "skip") return;
    const visible = active === 0 ? refA.current : refB.current;
    if (!visible) return;
    const tryPlay = () => playSafely(visible);
    // Try a few times — once immediately, then after frame paints,
    // then again 200/600 ms later. iOS sometimes refuses the first
    // call when the element JUST became visible.
    tryPlay();
    const raf = requestAnimationFrame(tryPlay);
    const t1 = window.setTimeout(tryPlay, 200);
    const t2 = window.setTimeout(tryPlay, 600);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [active, idxA, idxB, tier]);

  // Mobile autoplay safety net — first touch/pointer anywhere on
  // the page unblocks autoplay restrictions. We attach a one-shot
  // listener that force-plays the currently-visible hero video.
  useEffect(() => {
    if (tier === "skip" || typeof window === "undefined") return;
    const unblock = () => {
      const visible = activeRef.current === 0 ? refA.current : refB.current;
      playSafely(visible);
    };
    const opts = { once: true, passive: true } as AddEventListenerOptions;
    window.addEventListener("touchstart", unblock, opts);
    window.addEventListener("pointerdown", unblock, opts);
    return () => {
      window.removeEventListener("touchstart", unblock);
      window.removeEventListener("pointerdown", unblock);
    };
  }, [tier]);

  // FULL tier: warm the back buffer as soon as the first clip is
  // playable. Desktop wifi has the bandwidth.
  useEffect(() => {
    if (tier !== "full") return;
    const el = refA.current;
    if (!el) return;
    const ready = () => {
      setWarmBuffer(true);
      const b = refB.current;
      if (b) {
        try { b.load(); } catch {}
      }
    };
    if (el.readyState >= 2) { ready(); return; }
    el.addEventListener("loadeddata", ready, { once: true });
    el.addEventListener("canplay", ready, { once: true });
    return () => {
      el.removeEventListener("loadeddata", ready);
      el.removeEventListener("canplay", ready);
    };
  }, [tier]);

  // MOBILE-CYCLE tier: do NOT warm the back buffer eagerly. Wait until
  // the current clip is past 50% before promoting buffer's preload to
  // "auto" and calling .load(). This guarantees only ONE clip's bytes
  // are in flight at any moment.
  useEffect(() => {
    if (tier !== "mobile-cycle") return;
    const el = refA.current;
    if (!el) return;
    let armed = false;
    const onTime = () => {
      if (armed) return;
      if (!el.duration || el.duration === Infinity) return;
      if (el.currentTime / el.duration >= 0.5) {
        armed = true;
        setWarmBuffer(true);
        const b = refB.current;
        if (b) {
          try { b.load(); } catch {}
        }
      }
    };
    el.addEventListener("timeupdate", onTime);
    return () => el.removeEventListener("timeupdate", onTime);
  }, [tier, idxA]);

  const swapTo = (which: 0 | 1) => {
    activeRef.current = which;
    setActive(which);
    playSafely(which === 0 ? refA.current : refB.current);
  };

  const queueNextOn = (which: 0 | 1) => {
    const len = cycleSources.length;
    if (len === 0) return;
    const nextIdx = cursor.current % len;
    cursor.current = (cursor.current + 1) % len;
    // Delay the src swap until after the 350 ms crossfade completes.
    // Without this delay the idx/src change is batched with setActive in
    // the same render — React sees a new `src` on the just-became-inactive
    // element and (if key were present) would unmount it instantly, killing
    // the CSS opacity transition and causing a black flash. Even without key,
    // changing src while the element is still mid-fade-out can expose the
    // AVIF poster of the *next* clip at non-zero opacity. Waiting 360 ms
    // ensures the element is fully at opacity:0 before its src is touched.
    const FADE_MS = 360;
    window.setTimeout(() => {
      if (which === 0) setIdxA(nextIdx);
      else setIdxB(nextIdx);
      // For mobile-cycle, reset the warmBuffer flag so the next
      // buffer-warm cycle starts fresh.
      if (tier === "mobile-cycle") setWarmBuffer(false);
    }, FADE_MS);
  };

  const handleEnded = (which: 0 | 1) => {
    const other: 0 | 1 = which === 0 ? 1 : 0;
    const otherEl = other === 0 ? refA.current : refB.current;
    const ready = (otherEl?.readyState ?? 0) >= 3;

    if (ready) {
      try { if (otherEl) otherEl.currentTime = 0; } catch {}
      swapTo(other);
      queueNextOn(which);
    } else {
      // Back buffer not ready yet (slow mobile or longer clip):
      // restart the current clip to bridge the gap, swap when ready.
      const currentEl = which === 0 ? refA.current : refB.current;
      if (currentEl) {
        try { currentEl.currentTime = 0; } catch {}
        playSafely(currentEl);
      }
      if (otherEl) {
        const onReady = () => {
          otherEl.removeEventListener("canplay", onReady);
          if (activeRef.current === which) {
            try { otherEl.currentTime = 0; } catch {}
            swapTo(other);
            queueNextOn(which);
          }
        };
        otherEl.addEventListener("canplay", onReady);
      }
    }
  };

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  };

  const scrim = (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(180deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.18) 45%, rgba(0,0,0,0.48) 100%)",
      }}
    />
  );

  const wrapStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: 0,
  };

  // ─── Tier: skip — poster only, no <video> ────────────────────────
  if (tier === "skip") {
    return (
      <div aria-hidden style={wrapStyle}>
        <img
          src={globalPoster}
          alt=""
          aria-hidden
          decoding="async"
          fetchPriority="high"
          data-no-fade
          data-loaded="true"
          style={baseStyle}
        />
        {scrim}
      </div>
    );
  }

  const withSrc = (src: string | undefined) => (videoArmed ? src : undefined);

  // ─── Tier: single — one looping <video>, no A/B buffer ────────────
  if (tier === "single") {
    return (
      <div aria-hidden style={wrapStyle}>
        <video
          ref={refA}
          src={withSrc(desktopSet[0].src)}
          poster={globalPoster}
          autoPlay
          muted
          loop
          playsInline
          preload={videoArmed ? "auto" : "none"}
          onCanPlay={() => playSafely(refA.current)}
          style={baseStyle}
        />
        {scrim}
      </div>
    );
  }

  // ─── Tiers: mobile-cycle and full ────────────────────────────────
  // Both use the same A/B element shape. They differ in:
  //   * source pool (cycleSources, set above)
  //   * back-buffer warming (handled by the per-tier useEffect above)
  const srcA = withSrc(cycleSources[idxA]?.src);
  const srcB = withSrc(cycleSources[idxB]?.src);
  const posterA = cycleSources[idxA]?.poster ?? globalPoster;
  // posterB is only needed once the back-buffer is actually warming. Before
  // videoArmed, the B element doesn't exist in the DOM so its poster is never
  // fetched; after armed the poster is the same AVIF already in cache from
  // the A-side preload, so no extra network request is made.
  const posterB = cycleSources[idxB]?.poster ?? globalPoster;
  const preloadA = videoArmed ? "auto" : "none";
  const preloadB = videoArmed && warmBuffer ? "auto" : "none";

  return (
    <div aria-hidden style={wrapStyle}>
      <video
        ref={refA}
        src={srcA}
        poster={posterA}
        autoPlay
        muted
        playsInline
        preload={preloadA}
        onEnded={() => handleEnded(0)}
        onCanPlay={() => playSafely(refA.current)}
        style={{
          ...baseStyle,
          opacity: active === 0 ? 1 : 0,
          transition: "opacity .35s ease",
        }}
      />
      {/* B-buffer element is only rendered once video is armed. Before that,
          even a <video poster="..."> with preload="none" causes the browser to
          fetch the poster image (~17 KB on mobile). Deferring the element
          eliminates that unnecessary request from the LCP critical path. */}
      {videoArmed && (
        <video
          ref={refB}
          src={srcB}
          poster={posterB}
          autoPlay
          muted
          playsInline
          preload={preloadB}
          onEnded={() => handleEnded(1)}
          onCanPlay={() => playSafely(refB.current)}
          style={{
            ...baseStyle,
            opacity: active === 1 ? 1 : 0,
            transition: "opacity .35s ease",
          }}
        />
      )}
      {scrim}
    </div>
  );
};

export default HeroVideoBG;
